import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import deliveryApiParser, { type DeliveryProduct } from './deliveryApiParser';
import { MetaOption, LubricationChart, LubricationChartProduct } from '../../../types';

// ---------- Types ----------
export interface Delivery {
  id: string | number;
  srno: number;
  vessel: string;
  manager: string;
  port: string;
  date: string;
  deliveryDate?: string; // Raw deliveryDate from backend for edit mode
  supplier: string;
  lub_costs: string | number;
  ex_costs: string | number;
  ex_costs_fifo: number;
  type: string;
  deliveryType?: string; // Raw delivery type value (standard, adjusting, secondHand, newBuilding)
  invoiced: string;
  remarks: string;
}

export interface DeliveriesListResponse {
  data: Delivery[];
  pagination: number;
}

interface ExtraCostItem {
  extraCostId: number;
  value: number;
}

interface LineItem {
  productId: number;
  quantity: number;
  netValue: number;
  packagingCost: number;
  storageQuantity?: number;
  sumpTankQuantity?: number;
}

interface CreateDeliveryPayload {
  type: string;
  vesselId: number;
  managerId: number;
  portId: number;
  date: string;
  currencyId: number;
  supplierId: number;
  poNumber: string | null;
  invoiced: boolean;
  invoiceNumber: string;
  remarks: string;
  extraCosts: ExtraCostItem[];
  lineItems: LineItem[];
}

export interface GetDeliveriesListParams {
  [key: string]: any; // refine based on actual request params
}

// ---------- API Slice (merged) ----------
const deliveryApi = createApi({
  reducerPath: 'deliveryApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Delivery', 'Meta', 'Products', 'LubricationChart'],
  endpoints: (builder) => ({
    // Common ping URL to satisfy axiosBaseQuery; response is ignored by transformers
    // and replaced with local mock data until real endpoints are ready.

    // Grid list using axiosBaseQuery + transformResponse parser
    getDeliveriesList: builder.mutation<DeliveriesListResponse, GetDeliveriesListParams>({
      query: (params) => {
        return {
          url: '/deliveries',
          method: 'GET',
          params: params,
        };
      },
      transformResponse: (res: any): DeliveriesListResponse =>
        deliveryApiParser.DeliveriesListParser(res),
    }),

    // Products by Vessel (for adjusting and 2nd hand vessel)
    getProductsByVessel: builder.query<DeliveryProduct[], { vesselId: number }>({
      query: ({ vesselId }: { vesselId?: number }) => ({
        url: `/vessels/${vesselId}/products?status=active`,
        method: 'GET',
      }),
      transformResponse: (response: any): DeliveryProduct[] => {
        // Use the parser to handle the response
        const parsedProducts = deliveryApiParser.ProductsByVesselParser(response);

        // Return parsed products (empty array if no data)
        return parsedProducts;
      },
      providesTags: ['Products'],
    }),

    // Products by Vessel and Supplier (for standard and new building)
    getProductsByVesselAndSupplier: builder.query<
      DeliveryProduct[],
      { vesselId: number; supplierId: number }
    >({
      query: ({ vesselId, supplierId }) => ({
        url: `/vessels/${vesselId}/products/${supplierId}?status=active`,
        method: 'GET',
      }),
      transformResponse: (response: any): DeliveryProduct[] => {
        // Use the same parser to handle the response
        const parsedProducts = deliveryApiParser.ProductsByVesselParser(response);

        // Return parsed products (empty array if no data)
        return parsedProducts;
      },
      providesTags: ['Products'],
    }),

    // Delivery Products (for edit mode - prefilled products with values)
    getDeliveryProducts: builder.query<
      DeliveryProduct[],
      { deliveryId: number; vesselId: number; type: string }
    >({
      query: ({ deliveryId, vesselId, type }) => {
        // Map internal type to backend type (matching API documentation)
        const typeMap: Record<string, string> = {
          standard: 'STANDARD',
          adjusting: 'ADJUSTMENT',
          secondHand: 'ACQUISITION',
          newBuilding: 'NEW_BUILDING',
        };
        const backendType = typeMap[type] || type.toUpperCase();

        return {
          url: `/deliveries/${deliveryId}/vessel/${vesselId}/products?type=${backendType}`,
          method: 'GET',
        };
      },
      transformResponse: (response: any): DeliveryProduct[] => {
        // Use the parser to handle the response (with prefilled values)
        const parsedProducts = deliveryApiParser.DeliveryProductsParser(response);

        // Return parsed products (empty array if no data)
        return parsedProducts;
      },
      providesTags: ['Products'],
      keepUnusedDataFor: 0,
    }),

    // Inactive Products by Vessel (for ProductAddFlow step 1)
    getInactiveProductsByVessel: builder.query<LubricationChartProduct[], { vesselId: number }>({
      query: ({ vesselId }: { vesselId?: number }) => ({
        url: `/vessels/${vesselId}/products?status=inactive`,
        method: 'GET',
      }),
      transformResponse: (response: any): LubricationChartProduct[] => {
        // Use the new parser specifically for inactive products
        const parsedProducts = deliveryApiParser.InactiveProductsByVesselParser(response);

        // Return parsed products (empty array if no data)
        return parsedProducts;
      },
      providesTags: ['Products'],
    }),

    getLubricationChart: builder.query<LubricationChart, void>({
      query: () => ({
        url: '/lubrication-chart',
        method: 'GET',
      }),
      transformResponse: (res: any): LubricationChart => {
        if (res && res.data) {
          return res.data;
        }
        return {
          inactiveProducts: [],
          masterCatalogue: [],
          makers: [],
          categories: [],
          types: [],
          units: [],
        };
      },
      providesTags: ['LubricationChart'],
    }),

    // Create Delivery - Real API
    createDelivery: builder.mutation<any, CreateDeliveryPayload>({
      query: (payload) => ({
        url: '/deliveries',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['Delivery', 'Products'],
    }),

    // Update Delivery - Real API (for edit mode)
    updateDelivery: builder.mutation<
      any,
      { deliveryId: number; vesselId: number; payload: CreateDeliveryPayload }
    >({
      query: ({ deliveryId, vesselId, payload }) => ({
        url: `/deliveries/${deliveryId}/vessel/${vesselId}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['Delivery', 'Products'],
    }),

    // Delete Delivery - Real API
    deleteDelivery: builder.mutation<
      any,
      { deliveryId: number; vesselId: number; deliveryType: string }
    >({
      query: ({ deliveryId, vesselId, deliveryType }) => {
        // Map internal type to backend type (matching API documentation)
        const typeMap: Record<string, string> = {
          standard: 'STANDARD',
          adjusting: 'ADJUSTMENT',
          secondHand: 'ACQUISITION',
          newBuilding: 'NEW_BUILDING',
        };
        const backendType = typeMap[deliveryType] || deliveryType.toUpperCase();

        return {
          url: `/deliveries/${deliveryId}/${vesselId}/${backendType}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Delivery'],
    }),

    getSuppliersByManagerId: builder.query<MetaOption[], number>({
      query: (managerId: number) => ({
        url: `lubricant/suppliers/by-manager/${managerId}`,
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => deliveryApiParser.suppliersParser(res),
    }),

    getVesselFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/deliveries/vessels',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] =>
        deliveryApiParser.vesselFilterOptionsParser(res),
    }),

    getManagerFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/deliveries/managers',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] =>
        deliveryApiParser.managerFilterOptionsParser(res),
    }),

    getPortFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/deliveries/locations',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => deliveryApiParser.portFilterOptionsParser(res),
    }),

    getSupplierFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/deliveries/suppliers',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] =>
        deliveryApiParser.supplierFilterOptionsParser(res),
    }),

    getDeliveryTypesFilterOptions: builder.query<string[], void>({
      query: () => ({
        url: '/deliveries/delivery-types',
        method: 'GET',
      }),
      transformResponse: (res: any): string[] =>
        deliveryApiParser.deliveryTypesFilterOptionsParser(res),
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const deliveryReducer = deliveryApi.reducer;
export const deliveryMiddleware = deliveryApi.middleware;
export const deliveryReducerPath = deliveryApi.reducerPath;

// ---------- Hooks / Actions ----------
export const {
  useGetDeliveriesListMutation,
  useGetProductsByVesselQuery,
  useGetProductsByVesselAndSupplierQuery,
  useGetDeliveryProductsQuery,
  useGetInactiveProductsByVesselQuery,
  useGetLubricationChartQuery,
  useCreateDeliveryMutation,
  useUpdateDeliveryMutation,
  useDeleteDeliveryMutation,
  useGetSuppliersByManagerIdQuery,
  useGetVesselFilterOptionsQuery,
  useGetManagerFilterOptionsQuery,
  useGetPortFilterOptionsQuery,
  useGetSupplierFilterOptionsQuery,
  useGetDeliveryTypesFilterOptionsQuery,
} = deliveryApi;
